import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Package, Factory, Award, Globe, ChevronRight, Zap, Shield, Truck } from "lucide-react";
import { PRODUCTS } from "@/data/siteData";
import SEO from "@/components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }),
};

export default function HomePage() {
  const products = PRODUCTS.filter((p) => p.featured).slice(0, 4);
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "JSY Printing & Packaging",
    image: `${window.location.origin}/logo-color.svg`,
    url: window.location.origin,
    telephone: "+91-7003670034",
    address: {
      "@type": "PostalAddress",
      streetAddress: "B.B.T. Road, Maheshtala",
      addressLocality: "Kolkata",
      addressRegion: "West Bengal",
      postalCode: "700141",
      addressCountry: "IN",
    },
    areaServed: "India",
    sameAs: [],
  };

  return (
    <div data-testid="home-page">
      <SEO
        title="Plastic Packaging & Printing Solutions in Kolkata"
        description="JSY Printing & Packaging provides premium BOPP bags, polyester sheets, and readymade plastic packaging solutions for the textile industry across India."
        image="/images/about-factory-gate.png"
        structuredData={structuredData}
      />
      {/* ═══ HERO ═══ */}
  <section data-testid="hero-section" className="hero-gradient pt-16 pb-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={fadeUp}>
              <span className="overline" data-testid="hero-overline">We deliver excellence with simplicity</span>
              <h1
                className="mt-4 text-5xl lg:text-6xl tracking-tighter leading-[1.05]"
                style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}
              >
                Premium Plastic<br />
                <span className="text-[#1D4ED8]">Packaging and Printing</span> Solutions
              </h1>
              <p className="mt-6 text-lg text-[#71717A] leading-relaxed max-w-lg">
                BOPP bags, polyester sheets, and readymade packaging with high-quality printing crafted for the textile industry.
                Precision manufacturing. Global standards.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/products" data-testid="hero-cta-products" className="btn-primary">
                  Explore Products <ArrowRight size={16} />
                </Link>
                <Link to="/contact" data-testid="hero-cta-quote" className="btn-outline">
                  Request Quotation
                </Link>
              </div>
              {/* Stats inline */}
              <div className="mt-8 flex gap-8">
                {[
                  { val: "7+", label: "Years Experience" },
                  { val: "500+", label: "Clients Served" },
                  { val: "10M+", label: "Units / Month" },
                ].map((s) => (
                  <div key={s.label}>
                    <p className="text-3xl tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>{s.val}</p>
                    <p className="text-xs text-[#71717A] mt-1 tracking-wide uppercase">{s.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Hero Visual - Bento Grid */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="grid grid-cols-2 gap-3"
            >
              <div className="col-span-2 rounded overflow-hidden border border-[#E4E4E7]" style={{ height: '260px' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_0a2bc895-7b15-4ac3-bad9-8999ca920027/artifacts/jyki826a_WhatsApp%20Image%202026-04-12%20at%2012.48.02.jpeg"
                  alt="JSY Manufacturing"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded overflow-hidden border border-[#E4E4E7]" style={{ height: '180px' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_0a2bc895-7b15-4ac3-bad9-8999ca920027/artifacts/9gz9u6fa_WhatsApp%20Image%202026-04-12%20at%2012.48.02%20%282%29.jpeg"
                  alt="BOPP Bags"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded overflow-hidden border border-[#E4E4E7]" style={{ height: '180px' }}>
                <img
                  src="https://customer-assets.emergentagent.com/job_0a2bc895-7b15-4ac3-bad9-8999ca920027/artifacts/555eg9yz_WhatsApp%20Image%202026-04-12%20at%2012.48.01.jpeg"
                  alt="Machinery"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ WHY CHOOSE US ═══ */}
  <section data-testid="why-choose-section" className="py-10 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mb-8">
            <span className="overline">Why JSY</span>
            <h2 className="mt-3 text-4xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
              Built for Performance
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0">
            {[
              { icon: <Zap size={22} />, title: "Precision Manufacturing", desc: "State-of-the-art machinery ensuring consistent quality in every batch. Micron-level accuracy in film thickness." },
              { icon: <Shield size={22} />, title: "Quality Assured", desc: "Rigorous quality control at every production stage. ISO-standard processes from raw material to dispatch." },
              { icon: <Truck size={22} />, title: "On-Time Delivery", desc: "Streamlined logistics and in-house warehousing ensure your orders ship on schedule, every time." },
              { icon: <Globe size={22} />, title: "Export Ready", desc: "Meeting international packaging standards. Serving clients across domestic and global markets." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                data-testid={`why-card-${i}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="bento-card p-8"
              >
                <div className="w-10 h-10 rounded flex items-center justify-center bg-[#EBF0FF] text-[#1D4ED8] mb-5">
                  {item.icon}
                </div>
                <h3 className="text-base tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                  {item.title}
                </h3>
                <p className="text-sm text-[#71717A] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURED PRODUCTS ═══ */}
  <section data-testid="featured-products-section" className="py-10 px-6 bg-[#F4F5F7]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="flex items-end justify-between mb-8">
            <div>
              <span className="overline">Our Products</span>
              <h2 className="mt-3 text-4xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 700 }}>
                Featured Products
              </h2>
            </div>
            <Link to="/products" className="hidden md:flex items-center gap-1 text-sm text-[#1D4ED8] font-medium hover:gap-2 transition-all duration-200">
              View All <ChevronRight size={16} />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {products.map((p, i) => (
              <motion.div
                key={p.id}
                data-testid={`product-card-${i}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={i}
                variants={fadeUp}
                className="product-card bg-white rounded"
              >
                <div className="h-48 overflow-hidden">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                </div>
                <div className="p-5">
                  <span className="text-xs text-[#1D4ED8] font-medium tracking-wide uppercase">{p.category}</span>
                  <h3 className="mt-1 text-sm tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                    {p.name}
                  </h3>
                  <p className="mt-2 text-xs text-[#71717A] leading-relaxed line-clamp-2">{p.description}</p>
                  <Link
                    to={`/products?category=${encodeURIComponent(p.category)}`}
                    className="mt-3 inline-flex items-center gap-1 text-xs text-[#1D4ED8] font-medium hover:gap-2 transition-all duration-200"
                  >
                    View Details <ArrowRight size={12} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <Link to="/products" className="md:hidden btn-primary mt-8 mx-auto">
            View All Products <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ═══ CTA BANNER ═══ */}
  <section data-testid="cta-section" className="py-10 px-6 bg-[#09090B] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="overline" style={{ color: '#16A34A' }}>Ready to Get Started?</span>
            <h2 className="mt-4 text-4xl lg:text-5xl tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800 }}>
              Let's Build Your<br />Packaging and Printing Solution
            </h2>
            <p className="mt-4 text-[#A1A1AA] max-w-lg mx-auto">
              From concept to delivery, we provide end-to-end packaging and printing solutions tailored to your specific requirements.
            </p>
            <div className="mt-8 flex justify-center gap-3">
              <Link to="/contact" data-testid="cta-inquiry" className="btn-primary">
                Send Inquiry <ArrowRight size={16} />
              </Link>
              <Link to="/infrastructure" className="btn-outline" style={{ borderColor: '#27272A', color: '#A1A1AA' }}>
                Tour Our Factory
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
